// FlashTools - WebSocket client for the local native FTDI K-Line bridge.
// Mirrors the byte-pipe interface the Honda KWP protocol expects.

const hex = (arr) => Array.from(arr).map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join('');
const fromHex = (s) => new Uint8Array((s.match(/../g) || []).map((h) => parseInt(h, 16)));

// Lightweight one-shot probe: is the bridge up, and which J2534 DLLs did it find?
export async function probeBridge(url, timeout = 2500) {
  return new Promise((resolve) => {
    let ws;
    let done = false;
    const finish = (r) => { if (!done) { done = true; try { ws && ws.close(); } catch (_) {} resolve(r); } };
    const to = setTimeout(() => finish({ online: false }), timeout);
    try { ws = new WebSocket(url); } catch (_) { clearTimeout(to); return resolve({ online: false }); }
    ws.onopen = () => { try { ws.send(JSON.stringify({ id: 1, cmd: 'ping' })); } catch (_) {} };
    ws.onerror = () => { clearTimeout(to); finish({ online: false }); };
    ws.onmessage = (ev) => {
      clearTimeout(to);
      let m;
      try { m = JSON.parse(ev.data); } catch (_) { return finish({ online: true, dlls: [] }); }
      finish({ online: true, version: m.version, dlls: m.dlls || [] });
    };
  });
}

// A 2nd WebSocket closing makes the bridge ClosePort() the shared COM port, so NEVER probe while any
// Flash Tools session (this tab or another tab/window) is active.
export const BRIDGE_LOCK = { n: 0 };
const BUSY_KEY = 'tt_bridge_busy';
export const markBridgeBusy = () => { try { localStorage.setItem(BUSY_KEY, String(Date.now())); } catch (_) {} };
export const clearBridgeBusy = () => { try { localStorage.removeItem(BUSY_KEY); } catch (_) {} };
const bridgeBusy = () => {
  if (BRIDGE_LOCK.n > 0) return true;
  try { return Date.now() - Number(localStorage.getItem(BUSY_KEY) || 0) < 6000; } catch (_) { return false; }
};

// One-shot fetch of the COM ports the bridge currently sees (for the port picker).
export async function fetchBridgePorts(url, timeout = 2500) {
  if (bridgeBusy()) return { online: true, ports: [], busy: true };
  return new Promise((resolve) => {
    let ws; let done = false;
    const finish = (r) => { if (!done) { done = true; try { ws && ws.close(); } catch (_) {} resolve(r); } };
    const to = setTimeout(() => finish({ online: false, ports: [] }), timeout);
    try { ws = new WebSocket(url); } catch (_) { clearTimeout(to); return resolve({ online: false, ports: [] }); }
    ws.onopen = () => { try { ws.send(JSON.stringify({ id: 1, cmd: 'ports' })); } catch (_) {} };
    ws.onerror = () => { clearTimeout(to); finish({ online: false, ports: [] }); };
    ws.onmessage = (ev) => {
      clearTimeout(to);
      let m;
      try { m = JSON.parse(ev.data); } catch (_) { return finish({ online: true, ports: [] }); }
      const raw = m.ports || [];
      const ports = raw.map((p) => (typeof p === 'string'
        ? { port: p, name: p, kind: 'usb', conn: true }
        : { port: p.port, name: p.name || p.port, kind: p.kind || 'other', conn: p.conn !== false }));
      finish({ online: true, ports });
    };
  });
}

export class BridgeClient {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this._id = 0;
    this._pending = new Map();
  }

  connect() {
    return new Promise((resolve, reject) => {
      let settled = false;
      try {
        this.ws = new WebSocket(this.url);
      } catch (e) {
        return reject(new Error('BRIDGE_URL_INVALID'));
      }
      const to = setTimeout(() => {
        if (!settled) { settled = true; try { this.ws.close(); } catch (_) {} reject(new Error('BRIDGE_UNREACHABLE')); }
      }, 4000);
      this.ws.onopen = () => { if (!settled) { settled = true; clearTimeout(to); resolve(); } };
      this.ws.onerror = () => { if (!settled) { settled = true; clearTimeout(to); reject(new Error('BRIDGE_UNREACHABLE')); } };
      this.ws.onclose = () => {
        for (const [, p] of this._pending) p.reject(new Error('BRIDGE_CLOSED'));
        this._pending.clear();
      };
      this.ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (_) { return; }
        const p = this._pending.get(msg.id);
        if (!p) return;
        // Streamed progress (batch flash/read) or boot-window status: call the
        // callback, keep the promise open. Any interim frame lacks `ok`.
        if (msg.ok === undefined) { if (p.onProgress) p.onProgress(msg); return; }
        this._pending.delete(msg.id); p.resolve(msg);
      };
    });
  }

  _rpc(payload, timeoutMs = 8000, onProgress = null) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== 1) return reject(new Error('BRIDGE_NOT_OPEN'));
      const id = ++this._id;
      this._pending.set(id, { resolve, reject });
      const to = setTimeout(() => {
        if (this._pending.has(id)) { this._pending.delete(id); reject(new Error('BRIDGE_TIMEOUT')); }
      }, timeoutMs);
      const done = (fn) => (v) => { clearTimeout(to); fn(v); };
      const p = this._pending.get(id);
      p.resolve = done(resolve); p.reject = done(reject); p.onProgress = onProgress;
      this.ws.send(JSON.stringify({ id, ...payload }));
    });
  }

  async ping() { return this._rpc({ cmd: 'ping' }); }

  async open({ device, baud, dll, protocol, raw }) {
    const r = await this._rpc({ cmd: 'open', device, baud, dll, protocol, ...(raw ? { raw: 1 } : {}) }, 12000);
    if (!r.ok) throw new Error(r.error || 'bridge open failed');
    return r; // { dll, vbatt_mv }
  }

  async vbatt() {
    const r = await this._rpc({ cmd: 'vbatt' });
    return r.ok ? r.mv : 0;
  }

  // Trigger a K-Line hardware wake (UART BREAK LOW) on the bridge's open port.
  async wake(lowMs = 70, highMs = 140) {
    const r = await this._rpc({ cmd: 'wake', low: lowMs, high: highMs }, 4000);
    if (!r.ok) throw new Error(r.error || 'bridge wake failed');
  }

  // write bytes and read the response — returns Uint8Array (no TX echo via J2534)
  async writeRead(bytes, timeout = 400) {
    const r = await this._rpc({ cmd: 'wr', data: hex(bytes), timeout }, timeout + 6000);
    if (!r.ok) throw new Error(r.error || 'bridge wr failed');
    return fromHex(r.rx || '');
  }

  // KWP2000 fast-init: low/high pulse then StartComm + read, ATOMIC on the bridge (no RPC gap).
  async klineInit({ low = 25, high = 25, bytes, timeout = 400 } = {}) {
    const r = await this._rpc({ cmd: 'klineInit', low, high, data: hex(bytes), timeout }, timeout + 6000);
    if (!r.ok) throw new Error(r.error || 'bridge klineInit failed');
    return fromHex(r.rx || '');
  }

  // v2.0.42+: native cold-connect. The bridge runs the entire wake+init+read loop (bit-bang re-wake
  // + FE 04 FF FF + INIT + READ_ID) with tight native timing and returns the ID frame when found.
  async coldConnect({ baud = 10400, attempts = 20, reopenEvery = 2, readTo = 350 } = {}) {
    const r = await this._rpc({ cmd: 'coldConnect', baud, attempts, reopenEvery, readTo }, attempts * 2500 + 10000);
    if (!r.ok) throw new Error(r.error || 'bridge coldConnect failed');
    return { rx: fromHex(r.rx || ''), attempts: r.attempts || 0, trace: r.trace || '' };
  }

  // Yamaha YMETER init (bridge >= 1.9.37): the bridge polls the single-byte FD status @15625
  // natively until the ECU/meter answers with a valid frame, then returns it. No RPC gap.
  async yamahaInit({ window = 8000, poll = 90 } = {}) {
    const r = await this._rpc({ cmd: 'yamahaInit', window, poll }, window + 4000);
    if (!r.ok) throw new Error(r.error || 'yamahaInit failed');
    return { connected: !!r.connected, rx: fromHex(r.rx || ''), frame: fromHex(r.frame || ''), elapsedMs: r.elapsed_ms };
  }

  // Yamaha YMETER single command (FD/CE/CA/CB…) — returns the raw window (echo + reply).
  async yamahaCmd(bytes, window = 120) {
    const r = await this._rpc({ cmd: 'yamahaCmd', data: hex(bytes), window }, window + 4000);
    if (!r.ok) throw new Error(r.error || 'yamahaCmd failed');
    return fromHex(r.rx || '');
  }

  // Yamaha self-diagnosis scan (bridge >= 1.9.38): the bridge runs the CA/CB D-code scan natively
  // at YDT rapid-fire cadence and returns [{did,val}]. val=null when the CB read failed.
  async yamahaDiag({ maxPolls = 400, perCmd = 60 } = {}) {
    const r = await this._rpc({ cmd: 'yamahaDiag', maxPolls, perCmd }, 60000);
    if (!r.ok) throw new Error(r.error || 'yamahaDiag failed');
    return { polls: r.polls, items: (r.items || []).map((x) => ({ did: x.did, val: x.val < 0 ? null : x.val })) };
  }

  // Yamaha PID enumeration sweep (bridge >= 1.9.39): reads each parameter with `<idx> 01`. Returns
  // [{idx, flag, val, valid}] where valid = data-present bit (flag&0x80) and value != 0xFF.
  async yamahaPids({ loStart = 1, loEnd = 0x2F, hiStart = 0x80, hiEnd = 0x9F, perCmd = 50 } = {}) {
    const r = await this._rpc({ cmd: 'yamahaPids', loStart, loEnd, hiStart, hiEnd, perCmd }, 60000);
    if (!r.ok) throw new Error(r.error || 'yamahaPids failed');
    return (r.items || []).map((x) => ({ idx: x.idx, flag: x.flag, val: x.val, valid: (x.flag & 0x80) !== 0 && x.val !== 0xFF }));
  }

  async write(bytes, timeout = 200) {
    const r = await this._rpc({ cmd: 'w', data: hex(bytes), timeout }, timeout + 6000);
    if (!r.ok) throw new Error(r.error || 'bridge w failed');
  }

  // Yamaha DENSO B022 native FLASH read/write (bridge >= 1.9.74): the whole boot-window handshake
  // (wake + SecurityAccess + per-block read/write) runs at full FTDI speed inside the bridge.
  // onProgress gets {boot,round,opened,...} then {progress,total,elapsed_ms}.
  async yamahaFlashRead({ blocks = 72, blockSize = 512, startHi = 0x38, window = 30000 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'yamahaFlashRead', blocks, blockSize, startHi, window }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.why || r.error || 'yamahaFlashRead failed');
    return r;
  }
  async yamahaFlashWrite(bytes, { blocks = 72, blockSize = 512, startHi = 0x38, window = 30000 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'yamahaFlashWrite', data: hex(bytes), blocks, blockSize, startHi, window }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.why || r.error || 'yamahaFlashWrite failed');
    return r;
  }

  // Build the KWP session on the RUNNING ECU + start TesterPresent keepalive (bridge >= 1.9.79).
  // Call this while IG is ON, BEFORE the power-cycle; the keepalive holds the session across
  // OFF->ON so the subsequent yamahaFlashRead/Write security is accepted (no fresh StartComm).
  async yamahaSession() {
    const r = await this._rpc({ cmd: 'yamahaSession' }, 20000);
    if (!r.ok) throw new Error(r.error || 'yamahaSession failed');
    return r;
  }

  // Change the open port's baud on the fly (Fast Write). Requires bridge >= 1.2.
  async setBaud(baud) {
    const r = await this._rpc({ cmd: 'setbaud', baud }, 4000);
    if (!r.ok) throw new Error(r.error || 'bridge setbaud failed');
    return r;
  }

  // Batch flash (bridge >= 1.4): send ALL block frames at once; the bridge loops
  // writing them natively (no per-block round-trip) and streams progress. This is
  // what makes Fast Write reach high-speed speed. onProgress({progress,total,elapsed_ms}).
  async flashBlocks(framesHex, { blocklen = 139, timeout = 1500, retries = 10 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'flashblocks', blocks: framesHex, blocklen, timeout, retries }, 900000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'flashblocks failed'); e.failIndex = r.failIndex; e.rx = r.rx; throw e; }
    return r;
  }

  // Batch read (bridge >= 1.8): send ALL request frames at once; the bridge loops
  // WriteRead natively, parses 92 92 responses, and returns concatenated flash data
  // (hex). onProgress({progress,total,elapsed_ms}). Throws on old bridges (unknown cmd).
  async readBlocks(framesHex, { blocklen = 9, timeout = 1000, retries = 4 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'readblocks', blocks: framesHex, blocklen, timeout, retries }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.error || 'readblocks failed');
    return r; // { data: hex, read, elapsed_ms }
  }

  // Native Shindengen boot-read (bridge >= 1.9): the bridge runs the wake + Security
  // Access + read-probe loop natively at full port speed to catch the sub-second boot
  // window, then streams the whole dump. onProgress gets {progress,total,elapsed_ms}
  // during the dump and {boot:true,left_s,sec} during the boot window.
  async bootRead(size, { window = 90000, sec1 = '', sec2 = '', wakeLow = 70, wakeHigh = 140 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'bootRead', size, window, sec1, sec2, wakeLow, wakeHigh }, 3600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'bootRead failed'); e.why = r.why; e.lastProbe = r.lastProbe; e.secReplied = r.secReplied; throw e; }
    return r; // { data: hex, read, empty, elapsed_ms }
  }

  // Native Shindengen COLD read (bridge >= 1.9.7): runs the device-firmware cold
  // wake+init+Security-Access+read sequence with NO prior runtime/READ_ID session
  // (the 72 08 80 read engine only opens from a cold bootloader wake). The caller
  // opens the port then invokes this WITHOUT Connect's probe. onProgress gets
  // {boot:true,cold:true,wake,init,sec,probe} then {progress,total,elapsed_ms}.
  async coldRead(size, { sec1 = '', sec2 = '', fast = false } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'coldRead', size, sec1, sec2, fast: fast ? 1 : 0 }, 3600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'coldRead failed'); e.why = r.why; e.lastProbe = r.lastProbe; e.secReplied = r.secReplied; throw e; }
    return r; // { data: hex, read, empty, elapsed_ms }
  }

  // Native Shindengen Clear Flash Count (bridge >= 1.9.12): full proven handshake + SEC(DF) + CLEAR.
  // Returns the raw result {ok, clearReply, why, sec1, sec2, ...} — does NOT throw on ok:false so the
  // caller can log the exact clear reply / SEC state.
  async clearCount(sec1, sec2, clear, onProgress = null) {
    return await this._rpc({ cmd: 'clearCount', sec1, sec2, clear }, 120000, onProgress);
  }

  // Shindengen EEPROM (K1AA) cold read — 512B via native wake+SEC(DF)+word loop (bridge >= 1.9.15).
  async eepromRead({ sec1 = '', sec2 = '', words = 256 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'eepromRead', sec1, sec2, words }, 300000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'eepromRead failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin password-gated flash read (bridge >= 1.9.22) — full open sequence in native tight timing.
  async keihinColdRead({ size = 262144, basePage = 0, pw = '', blk = 12, variant = '', probePages = 0, turbo = 0 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'keihinColdRead', size, basePage, pw, blk, variant, probePages, turbo }, 3600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'keihinColdRead failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin K93 EEPROM FORMAT (bridge >= 1.9.43) — erase whole EEPROM to 0xFF (82 82 18 05) or 0x00
  // (82 82 19 05) after the same security/open, then read back 512 B for verification.
  async keihinEepromFormat({ pw = '', fill = 'ff' } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'keihinEepromFormat', pw, fill }, 600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'keihinEepromFormat failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin K93 EEPROM cold read (bridge >= 1.9.42) — same boot-window security/open as keihinColdRead,
  // then walks EEPROM byte-address 0x00..0xFF (82 82 10 06 <addr> -> 92 92 10 07 <d0> <d1>) = 512 B.
  async keihinEepromRead({ pw = '' } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'keihinEepromRead', pw }, 600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'keihinEepromRead failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin K93 EEPROM WRITE (bridge >= 1.9.47) — same security/open as keihinEepromRead, then per-word
  // write 82 82 14 08 <addr> <d0> <d1> -> ack 92 92 14 05, then read-back verify. data = 256/512 B hex.
  async keihinEepromWrite(dataHex, { pw = '', diff = false, zeroWords, marker } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'keihinEepromWrite', data: dataHex, pw, ...(diff ? { diff: 1 } : {}), ...(zeroWords ? { zeroWords } : {}), ...(marker ? { marker } : {}) }, 900000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'keihinEepromWrite failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin clear flash count (bridge >= 1.9.48) — boot-window security then zero page 0x14 (addr 0x00..0x7F).
  async keihinClearCount({ pw = '', words, marker, fast } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'keihinClearCount', pw, ...(words ? { words } : {}), ...(marker ? { marker } : {}), ...(fast ? { fast: 1 } : {}) }, 300000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'keihinClearCount failed'); e.why = r.why; throw e; }
    return r;
  }

  // Keihin R8C 48KB fast batch write (bridge >= 1.9.49). Returns the raw result (ok/failIndex/rx)
  // so the caller can report a precise failure — does NOT throw on a bridge-side !ok.
  async keihinR8CBlocks(framesHex, { blocklen = 139, timeout = 1500, retries = 6 } = {}, onProgress = null) {
    return this._rpc({ cmd: 'keihinR8CBlocks', blocks: framesHex, blocklen, timeout, retries }, 3600000, onProgress);
  }

  async densoBlocks(framesHex, { blocklen = 139, timeout = 1500, retries = 6, query = 0 } = {}, onProgress = null) {
    return this._rpc({ cmd: 'densoBlocks', blocks: framesHex, blocklen, timeout, retries, query }, 3600000, onProgress);
  }

  // Renesas R8C/M16C bench read over plain FTDI TTL (bridge >= 1.9.50). Opens raw UART (no K-Line
  // wake), auto-bauds (0x00 -> 0xB0), reads version + flash pages. Returns { ok, locked, version,
  // idsr, data (hex), read, allff_pages, pages }.
  async renesasRead({ device, baud = 9600, start = 0x8000, size = 0x10000, idhex = '', idaddr } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'renesasRead', device, baud, start, size, idhex, idaddr }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.error || 'renesasRead failed');
    return r;
  }

  // RH850 2-wire UART flash read (bridge >= 2.1.24). Port must already be open + handshaked at the
  // negotiated baud (JS does sync+0x38+0x32+0x34+0x00+0x2C). The bridge natively runs read-prep
  // (0x3A + 0x71 FF) + Read 0x15 + 0x400-byte data-frame loop with correct 2-byte-length framing.
  async rh850Read({ start, end } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'rh850Read', start, end }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.error || 'rh850Read failed');
    return r; // { data: hex, read, blank, elapsed_ms }
  }

  // RH850 flash WRITE (bridge >= 2.1.25). DESTRUKTIF, agent-derived. Port sudah open+handshake.
  // Native: prep(3A,71) -> [erase 0x12] -> write 0x13 + data 0x400/frame -> [verify read-back].
  async rh850Write({ start, end, data, erase = true, verify = true } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'rh850Write', start, end, data, erase: erase ? 1 : 0, verify: verify ? 1 : 0 }, 3600000, onProgress);
    if (!r.ok) throw new Error(r.error || 'rh850Write failed');
    return r; // { written, verified, mismatch }
  }

  // RH850 Erase-PROBE (bridge >= 2.1.31): coba beberapa format perintah erase, lapor per format.
  async rh850EraseProbe(onProgress = null) {
    const r = await this._rpc({ cmd: 'rh850EraseProbe' }, 300000, onProgress);
    return r; // { ok, found } atau { ok:false, error }
  }

  // Renesas R8C/M16C WRITE (bridge >= 1.9.51): erase-all -> page program -> optional verify.
  // Returns the full result (ok may be false with error/mismatch detail) — does NOT throw on !ok.
  async renesasWrite({ device, baud = 9600, start = 0x8000, data, erase = true, verify = true, idhex = '', idaddr, skipBlank } = {}, onProgress = null) {
    const payload = { cmd: 'renesasWrite', device, baud, start, data, erase: erase ? 1 : 0, verify: verify ? 1 : 0, idhex, idaddr };
    if (skipBlank != null) payload.skipBlank = skipBlank ? 1 : 0;
    return this._rpc(payload, 3600000, onProgress);
  }

  // Tactrix OpenPort 2.0 / J2534 CAN-UDS write (bridge >= 1.9.52, Windows x86 + op20pt32.dll).
  // Streams {phase} and {progress,total} frames while flashing. `data` is the full BIN hex; the
  // bridge writes region [addr, addr+size) in `block`-byte transfers. Returns the raw result
  // ({ ok, ecuId, seed, key, blocks, written } or { ok:false, error }). Does NOT throw on !ok.
  async tactrixWrite({ data, addr = 0x10000, size = 0xF0000, block = 1024, arm = false, keydef = '', offset = 0x200 } = {}, onProgress = null) {
    return this._rpc({ cmd: 'tactrixWrite', data, addr, size, block, arm: arm ? 1 : 0, keydef, offset }, 1800000, onProgress);
  }

  // Kawasaki ZX-25R CAN write (KWP2000-over-ISO15765, bridge >= 2.0.23). Full BIN hex; bridge
  // flashes region [addr, addr+size) S-box encoded in 2048B blocks, then validation checksum.
  async kawasakiZxWrite({ data, addr = 0x30000, size = 0x1D0000, block = 2048, arm = false, cks = '93C7' } = {}, onProgress = null) {
    return this._rpc({ cmd: 'kawasakiZxWrite', data, addr, size, block, arm: arm ? 1 : 0, cks }, 2400000, onProgress);
  }

  // Kawasaki ZX-25R read-only ECU info over CAN (bridge >= 2.0.30): 10 80 + 1A / 1A 81 / 1A 80.
  // Returns { ok, hw, sw, vin }. Non-destructive — no security, no erase. Does NOT throw on !ok.
  async kawasakiZxInfo() {
    return this._rpc({ cmd: 'kawasakiZxInfo' }, 60000);
  }

  // Kawasaki ZX-25R SAFE unlock test (bridge >= 2.0.31): session + security access only, then stop.
  // No erase/write. Returns { ok, unlocked, seed, key, nrc }. Does NOT throw on !ok.
  async kawasakiZxUnlock(onProgress = null) {
    return this._rpc({ cmd: 'kawasakiZxUnlock' }, 120000, onProgress);
  }

  // Tactrix read-only ECU identification (bridge >= 1.9.52): 10 03 + 22 F1 81 / 22 F1 00 / seed.
  // Returns { ok, ecuId, f100, seed }. Does NOT throw on !ok.
  // Denso Sahara 300 CAN read-only (BA 01, bridge >= 2.0.69). probe=1 → 3 samples only.
  async saharaRead({ probe = false, size = 0xC0000, variant = 0 } = {}, onProgress = null) {
    return this._rpc({ cmd: 'saharaRead', probe: probe ? 1 : 0, size, variant }, 3600000, onProgress);
  }

  // Sahara staged read-only diagnosis (bridge >= 2.0.70): sessions 01/03/02 → 27 01 seed only → BA 01.
  async saharaDiag(onProgress = null) {
    return this._rpc({ cmd: 'saharaDiag' }, 120000, onProgress);
  }

  // Sahara session-03 Security Access (one key variant per power-cycle) + BA read test (bridge >= 2.0.73).
  async saharaUnlock(variant = 0, onProgress = null) {
    return this._rpc({ cmd: 'saharaUnlock', variant }, 120000, onProgress);
  }

  async tactrixInfo() {
    return this._rpc({ cmd: 'tactrixInfo' }, 20000);
  }

  // Tactrix SAFE security test (bridge >= 1.9.57): 10 03 → 27 01 → 27 02 then stop (no erase/write).
  // key = keyhex (explicit) or (seed + offset). Returns { ok, seed, key, unlocked, nrc }.
  async tactrixSecTest({ offset = 0x200, keyhex = '', seedonly = false, keydef = '' } = {}) {
    return this._rpc({ cmd: 'tactrixSecTest', offset, keyhex, seedonly: seedonly ? 1 : 0, keydef }, 30000);
  }

  // Shindengen EEPROM cold write — 512B image, per-word read-back verify (bridge >= 1.9.15).
  async eepromWrite(dataHex, { sec1 = '', sec2 = '', words = 256 } = {}, onProgress = null) {
    const r = await this._rpc({ cmd: 'eepromWrite', data: dataHex, sec1, sec2, words }, 600000, onProgress);
    if (!r.ok) { const e = new Error(r.error || 'eepromWrite failed'); e.why = r.why; e.word = r.word; throw e; }
    return r;
  }

  // Force-close the socket NOW to interrupt an in-flight native batch (flashblocks/
  // readblocks). The batch runs synchronously inside the bridge, so the only way to
  // stop it from the browser is to drop the socket: onclose rejects the pending RPC
  // with BRIDGE_CLOSED (unwinding the await) and the bridge closes its FTDI port, so
  // its batch loop bails on the next writeRead. Used by Abort during Fast Write/read.
  abort() {
    try { this.ws && this.ws.close(); } catch (_) {}
  }

  // Close only the serial/FTDI port on the bridge, KEEPING the WebSocket alive so the
  // same session can re-open the port (RH850 needs a re-open after an ECU reset).
  async closePort() {
    try { await this._rpc({ cmd: 'close' }, 3000); } catch (_) {}
  }

  async close() {
    try { await this._rpc({ cmd: 'close' }, 3000); } catch (_) {}
    try { this.ws && this.ws.close(); } catch (_) {}
    this.ws = null;
  }
}
